/*
//-------------------------------------------------------
//   Copyright (c) 2019 Templa Computer Systems Ltd
//
//   App:        EmployeePortal.UI
//   Object:     MyDetails
//   Details:    My details store
//
// Log      Date   By  Scan Description
// -----  -------- --- ---- ------------------------------
// 101886 02/07/19 JHT M01  Added image upload
// 106537 04/03/20 JC  M02  Clear errors on start of details saving
// 103839 06/04/20 JC  M03  Added refresh timestamp
// 108107 01/06/20 JC  M04  Moved most logic to store
//-------------------------------------------------------
*/

import { fetchActions } from "../FetchWrapper";
import { userActions } from './Authentication';

const detailsRequestType = 'DETAILS_REQUEST';
const detailsSuccessType = 'DETAILS_SUCCESS';
const detailsFailureType = 'DETAILS_FAILURE';
const detailsClearType = 'DETAILS_CLEAR';
const detailsSavingType = "DETAILS_SAVING";
const detailsSavingFailureType = "DETAILS_SAVINGFAILURE";

const detailsSetEditType = "DETAILS_SETEDIT"; //M04
const detailsCancelEditType = "DETAILS_CANCELEDIT"; //M04

const detailsUpdateChangedFieldType = "DETAILS_UPDATECHANGEFIELD"; //M04

const detailsSetImageFileType = "DETAILS_SETIMAGEFILE"; //M04
const detailsSetImageFileStoreLoadedFileType = "DETAILS_SETIMAGEFILESTORELOADEDFILETYPE"; //M04
const detailsSetImageFileNoFileSelectedType = "DETAILS_SETIMAGEFILENOFILESELECTEDTYPE"; //M04
const detailsClearImageFileType = "DETAILS_CLEARIMAGEFILE"; //M04

const imageKey = "~EPChange~image" //M04

const initialState = {
    fields: [],
    isLoading: false,
    canRequest: true,
    error: undefined,
    timestamp: 0, //M03
    editing: false,  //M04,
    changedFields: {}, //M04
    imageFile: null, //M04
    canPreviewFile: false, //M04
    hasSaveError: false //M04
};

export const detailsActions = {
	get,
	refresh,
	save,
    clear,

    setEdit, //M04
    cancelEdit, //M04

    updateChangedField, //M04

    setImageFile, //M04
    setImageFile_NoFileSelected, //M04
    setImageFile_StoreLoadedFile, //M04
    clearImageFile //M04
};

function get() {
	return (dispatch, getState) => {
        if (!getState().myDetails.canRequest) {
            //no changed fields so cancel, but do not refresh detals
            dispatch(cancelEdit(false));
            return
        }

		dispatch(refresh());
	};
}

function refresh() {
	return (dispatch, getState) => {

		if (!getState().clientConfiguration.isLoaded)
			return;

		if (!getState().clientConfiguration.config.endpoint && !getState().authentication.user)
			return;

		dispatch(_request());

		const url = getState().clientConfiguration.config.endpoint + "/employeedetails";
		const ticket = getState().authentication.user.ticket;

		fetchActions.get(url, ticket)
			.then(
				response => {
					datesToHtml(response.data);
					dispatch(_success(response.data));
				},
				error => {
					if (error.sessionExpired) {
						dispatch(userActions.sessionExpired());
					}
					else {
						var message = `Could not get my details: ${error.message}`;
						dispatch(_failure(message));
					}
				}
			);
	};

	function datesToHtml(fields) {
		return fields.map((item, i) => {
			if (item.dataType === 3 || item.dataType === 4) // Date and DateTime
				item.value = item.value.replace(/\//g, "-");
			return item;
		});
	}

	function _request() { return { type: detailsRequestType }; }
	function _success(fields) { return { type: detailsSuccessType, fields }; }
	function _failure(error) { return { type: detailsFailureType, error }; }
}


// --------------------------------------------------------------
// Save from MyDetails
// M01 added 'hasImage'
function save() {
	return (dispatch, getState) => {
		if (!getState().clientConfiguration.config.endpoint)
            return;

        // get changed fields from state
        let { changedFields } = getState().myDetails;

        // if any changes in state, force save 
        if (changedFields.length !== 0) { //M03

            dispatch(_saving());

            let hasImage = getState().myDetails.imageFile !== null;

            //set URL and auth ticket
            const url = getState().clientConfiguration.config.endpoint + "/employeedetails";
            const ticket = getState().authentication.user.ticket;
            var data = {};

            //build 'data' array from list of changedField
            Object.keys(changedFields).forEach(function (key) {

                //*M01 weed out image if no image selected (to prevent overwrite of prev stored image)
                var bSkip = false;
                bSkip = !hasImage && key === imageKey;
                if (!bSkip) {
                    var field = changedFields[key];
                    var value = dateToApi(field);
                    data[key] = value;
                }
            });

            //post to the API
            fetchActions.post(url, ticket, data)
                .then(
                    response => {
                        if (response.data) {
                            var message = `Error saving detail: ${response.data}`;
                            dispatch(_failure(message));
                        }
                        setTimeout(function () {
                            dispatch(cancelEdit(false)); //tidy up via cancel edit
                            dispatch(detailsActions.refresh());
                        }, 500);
                    },
                    error => {
                        if (error.sessionExpired) {
                            dispatch(userActions.sessionExpired());
                        }
                        else {
                            var message = `Could not save detail: ${error.message}`;
                            dispatch(_failure(message));
                        }
                    }
                );
        } else {
            //no changed fields so cancel, refreshing details
            dispatch(cancelEdit(true));
        }
	};

	function dateToApi(item) {
		if (item.type === 3 || item.type === 4) // Date and DateTime
			return item.value.replace(/-/g, "/");
		return item.value;
	}

	function _saving() { return { type: detailsSavingType }; }
    function _failure(error) { return { type: detailsSavingFailureType, error }; } //M04 was detailsFailureType
}

function clear() {
	return (dispatch) => {
		dispatch(_clear());
	};

	function _clear() { return { type: detailsClearType }; }
}

//M04 set edit
function setEdit() {
    return (dispatch) => {
        dispatch(_setEdit());
    };

    function _setEdit() { return { type: detailsSetEditType }; }
}

//M04 cancel edit
function cancelEdit(refresh) {
    return (dispatch) => {
        //cancel edit
        dispatch(_cancelEdit());

        //if set to refresh
        if (refresh) {
            //and re-get details
            dispatch(get());
        }
    };

    function _cancelEdit() { return { type: detailsCancelEditType }; }
}

//M04 update changed field
function updateChangedField(item) {
    return (dispatch, getState) => {

        //declare new changed fields object
        let newChangedFields = {};

        //determine if we've found a match
        let bMatchFound = false;

        //get the current changed fields
        const { changedFields } = getState().myDetails;

        //go through each changed field
        Object.keys(changedFields).forEach(function (key) {

            // if the key matches
            if (key === item.key) {

                //flag as match found
                bMatchFound = true;

                //if changed
                if (item.isChanged) {
                    // if changed, add to new changed fields
                    newChangedFields[key] = { value: item.value, dataType: item.dataType, requiresImageOnChange: item.requiresImageOnChange };
                }
                else {
                    //do nothing as will not be copied over, i.e. deleted
                }
            } else {
                //doesn't match so copy over anyway
                newChangedFields[key] = { ...changedFields[key] };
            }
        });


        //not found & item is changed so add
        if (!bMatchFound && item.isChanged) {
            newChangedFields[item.key] = { value: item.value, dataType: item.dataType, requiresImageOnChange: item.requiresImageOnChange };
        }

        // apply new list back to state
        dispatch(_updateChangedField( newChangedFields));
    };

    function _updateChangedField(changedFields) { return { type: detailsUpdateChangedFieldType, changedFields }; }
}

//M04 set image file
function setImageFile(fileToRead) {
    return (dispatch) => {
        
        //determine if can preview file -- accepted file types
        let bcanPreviewFile = (fileToRead && (fileToRead.type == "image/jpeg" || fileToRead.type == "image/png" || fileToRead.type == "image/gif"));

        // get local URL for the image preview, storing in state object
        dispatch(_setImageFile(URL.createObjectURL(fileToRead), bcanPreviewFile ));

    };

    function _setImageFile(imageFile, canPreviewFile) {
        return { type: detailsSetImageFileType, imageFile, canPreviewFile };
    }
}

//M04 set image file - store loaded file type
function setImageFile_StoreLoadedFile(readerResult) {
    return (dispatch, getState) => {

        // ... get the changedfields from state, and append with the image path
        var changedFields = getState().myDetails.changedFields;

        changedFields[imageKey] = { value: readerResult, dataType: "1", requiresImageOnChange: false };

        // ... return changedFields to state
        dispatch(_storeLoadedFile( changedFields ));

    };

    function _storeLoadedFile(changedFields) {
        return { type: detailsSetImageFileStoreLoadedFileType, changedFields: changedFields };
    }
}

//M04 set image file
function setImageFile_NoFileSelected() {
    return (dispatch) => {

        //no file selected 
        dispatch(_noFileSelected());
    };

   
    function _noFileSelected() { return { type: detailsSetImageFileNoFileSelectedType }; }
}

//M04 clearImageFile
function clearImageFile() {
    return (dispatch, getState) => {

        //declare new changed fields object
        let newChangedFields = {};

        // ... get the changedfields from state, and append with the image path
        var changedFields = getState().myDetails.changedFields;


        //go through each changed field
        Object.keys(changedFields).forEach(function (key) {

            // if the ep change key
            if (key === imageKey) {

                //do nothing as will not be copied over, i.e. deleted

            } else {
                //doesn't match so copy over anyway
                newChangedFields[key] = { ...changedFields[key] };
            }
        });

        //clear image file
        dispatch(_clearImageFile(newChangedFields));
    };

    function _clearImageFile(changedFields) { return { type: detailsClearImageFileType, changedFields }; }
}


export const reducer = (state, action) => {
	state = state || initialState;

	if (action.type === detailsRequestType) {
        return { ...state, isLoading: true, canRequest: false, editing: false, hasSaveError: false }; //M04
	}

	if (action.type === detailsSavingType) {
        return { ...state, isLoading: true, canRequest: false, error: undefined, hasSaveError: false }; //M02 clear error M04
	}

	if (action.type === detailsSuccessType) {
        return {
            ...state, isLoading: false, canRequest: false, fields: action.fields, timestamp: Date.now(), editing: false, hasSaveError: false
        }; //M03 M04
	}

	if (action.type === detailsFailureType) {
		return { ...state, isLoading: false, canRequest: true, error: action.error};
    }

    //M04 error saving
    if (action.type === detailsSavingFailureType) {
        return { ...state, isLoading: false, canRequest: true, error: action.error, hasSaveError: true };
    }

	if (action.type === detailsClearType) {
		return { ...state, isLoading: false, canRequest: true, fields: [] };
    }

    //M04 set edit - simply sets editing flag to true
    if (action.type === detailsSetEditType) {
        return { ...state, editing: true };
    }

    //M04 cancel edit - return state to before any editing took place
    if (action.type === detailsCancelEditType) {
        return { ...state, editing: false, changedFields: {}, imageFile: null, canPreviewFile: false, error: undefined, hasSaveError: false};
    }

    //M04 update changed fields
    if (action.type === detailsUpdateChangedFieldType) {
        return { ...state, changedFields: action.changedFields };
    }

    //M04 set image file
    if (action.type === detailsSetImageFileType) {
        return { ...state, imageFile: action.imageFile, hasSaveError: false, canPreviewFile: action.canPreviewFile };
    }

    //M04 set image file - store loaded file type
    if (action.type === detailsSetImageFileStoreLoadedFileType) {
        return { ...state, changedFields: action.changedFields };
    }

    //M04 set image file - no file selected
    if (action.type === detailsSetImageFileNoFileSelectedType) {
        return { ...state, imageFile: null, canPreviewFile: false };
    }

    //M04 clear image file
    if (action.type === detailsClearImageFileType) {
        return { ...state, imageFile: null, canPreviewFile: false, hasSaveError: false, changedFields: action.changedFields };
    }

	return state;
};